import React, { useEffect, useState } from 'react';
import {
  MAX_REMINDER_LEAD_HOURS, NO_REMINDERS, REMINDER_LEAD_OPTIONS, isCustomLead,
} from '../../../domain/categories';
import s from './categories.module.css';

// One reminder-lead dropdown — MMS's "Email Reminders" or "SMS Reminders".
//
// As in MMS's Calendar → Options → Categories & Locations → Edit Category
// (#87):
//
//   * a single select, options in this order: No Reminders, 1 hour before,
//     2 hours before, 3 hours before, 1 day before, 2 days before, Custom…
//   * choosing `Custom…` replaces the dropdown with a number field whose unit
//     is the static text "hours before", so a custom lead is a whole number of
//     hours.
//
// A stored value that equals a stock option is shown as that option rather
// than as a custom number, so a teacher who typed 24 into Custom sees "1 day
// before" next time. What MMS does in that case is unchecked.
//
// `Custom…` is a mode, not a value: held here, because "the teacher has
// opened the number box" is not a fact the category record should carry.
//
// The value is reported as typed and on every keystroke (#131), so the
// drawer's draft is the number entered and `categoryErrors` can refuse it on
// Save, as MMS refuses the save and says why. `normaliseLeadHours` still guards
// the read path and `firestore.rules` bounds the document, because the server
// sizes the reminder sweep's window from this number whatever a client sent.
//
// `error` is what the last Add or Save refused, passed down: the refusal
// arrives on submit, not mid-number (#116).

const CUSTOM = 'custom';

// The box's text for a stored value. Whole numbers only, so `String` is the
// whole conversion; null and undefined are the empty box.
const textOf = (value) => (value == null ? '' : String(value));

// The value a typed box reports upward. Empty is No Reminders, which the
// Cancel beside the box commits deliberately.
const valueOf = (text) => {
  const trimmed = String(text ?? '').trim();
  if (!trimmed) return NO_REMINDERS;
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return NO_REMINDERS;
  return Math.round(n);
};

export default function ReminderLeadField({
  label, value, onChange, disabled = false, id, error = null,
  // A line under the field saying why it cannot be used — SMS Reminders'
  // "Text messages aren't available yet" (#782).
  note = null,
}) {
  const [custom, setCustom] = useState(() => isCustomLead(value));
  const [draft, setDraft] = useState(() => textOf(value));

  // Follow the record when it changes underneath — another tab, or this
  // control's save coming back through the snapshot. Guarded on the draft
  // parsing to the incoming value rather than matching it as text, so a box
  // being typed in is left alone: "007" and "7" are the same lead.
  useEffect(() => {
    if (valueOf(draft) === value) return;
    setCustom(isCustomLead(value));
    setDraft(textOf(value));
    // The draft is deliberately not a dependency: this follows the record, and
    // re-running it on every keystroke is the fight it exists to avoid.
  }, [value]);

  const pick = (raw) => {
    if (raw === CUSTOM) {
      setCustom(true);
      // Nothing is saved yet: the mode has changed, the lead has not. Seeding
      // the box with the current lead means the teacher edits a number rather
      // than facing an empty field.
      setDraft(textOf(value));
      return;
    }
    setCustom(false);
    setDraft(raw);
    onChange(raw === '' ? NO_REMINDERS : Number(raw));
  };

  const typed = (text) => {
    setDraft(text);
    onChange(valueOf(text));
  };

  const stock = REMINDER_LEAD_OPTIONS.some((o) => o.hours === value);
  const selected = custom ? CUSTOM : (stock && value !== NO_REMINDERS ? String(value) : '');

  return (
    <div className={s.leadField}>
      <label className={s.leadLabel} htmlFor={id}>{label}</label>
      {custom ? (
        <div className={s.leadCustom}>
          <input
            id={id}
            className={error ? s.leadNumberError : s.leadNumber}
            type="number"
            min={1}
            max={MAX_REMINDER_LEAD_HOURS}
            value={draft}
            disabled={disabled}
            aria-label={`${label}, hours before`}
            aria-invalid={error ? 'true' : undefined}
            onChange={(e) => typed(e.target.value)}
          />
          {/* Static text, exactly as MMS has it. The unit is not a choice. */}
          <span className={s.leadUnit}>hours before</span>
          <button
            type="button"
            className={s.leadPlain}
            disabled={disabled}
            onClick={() => { setCustom(false); setDraft(''); onChange(NO_REMINDERS); }}
          >
            Cancel
          </button>
        </div>
      ) : (
        <select
          id={id}
          className={s.leadSelect}
          value={selected}
          disabled={disabled}
          onChange={(e) => pick(e.target.value)}
        >
          {REMINDER_LEAD_OPTIONS.map((o) => (
            <option key={String(o.hours)} value={o.hours === NO_REMINDERS ? '' : String(o.hours)}>
              {o.label}
            </option>
          ))}
          <option value={CUSTOM}>Custom…</option>
        </select>
      )}
      {error ? <p className={s.leadError}>{error}</p> : null}
      {note}
    </div>
  );
}
